/** Helpers for inbox reply composer — plain text ↔ HTML conversion. */
import DOMPurify from "dompurify";

const COMPOSER_ALLOWED_TAGS = [
  "a", "b", "i", "em", "strong", "u", "p", "br", "ul", "ol", "li",
  "blockquote", "div", "span",
];

const COMPOSER_ALLOWED_ATTR = ["href", "target", "rel"];

/**
 * Convert HTML to plain text, preserving list structure for WhatsApp/Telegram.
 */
export function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  if (typeof document === "undefined") {
    return html
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<[^>]+>/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("ul").forEach((ul) => {
    ul.querySelectorAll(":scope > li").forEach((li) => {
      li.insertBefore(doc.createTextNode("• "), li.firstChild);
    });
  });
  doc.querySelectorAll("ol").forEach((ol) => {
    Array.from(ol.children)
      .filter((node) => node.tagName === "LI")
      .forEach((li, index) => {
        li.insertBefore(doc.createTextNode(`${index + 1}. `), li.firstChild);
      });
  });
  doc.querySelectorAll("br").forEach((br) => {
    br.replaceWith(doc.createTextNode("\n"));
  });
  doc.querySelectorAll("p, div, li, blockquote, h1, h2, h3, h4, h5, h6").forEach((el) => {
    el.appendChild(doc.createTextNode("\n"));
  });
  return (doc.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function plainTextToHtml(text) {
  const raw = (text || "").trim();
  if (!raw) return "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Single <p> + <br> keeps a lossless round-trip with stripHtml / char counts.
  // Splitting on blank lines into multiple <p> tags caused view→edit counts to jump
  // because browsers insert extra newlines between block elements.
  return `<p>${escaped.replace(/\n/g, "<br>")}</p>`;
}

/**
 * Canonical draft character count (plain text only — never HTML tags / editor markup).
 * Use the same helper for preview, edit mode, save, and regenerate.
 */
export function countDraftCharacters({ bodyText = "", bodyHtml = "" } = {}) {
  const html = (bodyHtml || "").trim();
  if (html) {
    const fromHtml = stripHtml(html);
    if (fromHtml) return fromHtml.length;
  }
  return (bodyText || "").trim().length;
}

/** Live editor count from contentEditable HTML (same rules as countDraftCharacters). */
export function countDraftCharactersFromHtml(html) {
  return stripHtml(html || "").length;
}

export function logDraftCharCount(event, detail = {}) {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.info(`[draft-chars] ${event}`, detail);
}

export function sanitizeComposerHtml(html) {
  if (!html || typeof html === "undefined") return "";
  if (typeof window === "undefined") return stripHtml(html);
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: COMPOSER_ALLOWED_TAGS,
    ALLOWED_ATTR: COMPOSER_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
  DOMPurify.removeHook("afterSanitizeAttributes");
  return clean;
}

export function buildQuotedReplyHtml(inboundText, fromName) {
  const plain = (inboundText || "").trim();
  if (!plain) return "";
  const header = fromName ? `${fromName} wrote:` : "Previous message:";
  const escaped = plain
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
  return `<blockquote><p><strong>${header}</strong></p><p>${escaped}</p></blockquote><p><br></p>`;
}

export function initialComposerHtml({ bodyHtml, bodyText, signature }) {
  const text = (bodyText || "").trim();
  const html = (bodyHtml || "").trim();
  if (html && stripHtml(html)) return html;
  const parts = [];
  if (text) parts.push(plainTextToHtml(text));
  if (signature?.trim()) {
    parts.push(`<p><br></p><p>${signature.trim().replace(/\n/g, "<br>")}</p>`);
  }
  return parts.join("");
}

/**
 * Resolve editable reply body from a draft/outbound message.
 * Thread subject and headers are handled by the backend — never surfaced here.
 */
export function resolveReplyComposerContent(message) {
  if (!message) {
    return { bodyText: "", bodyHtml: "" };
  }
  const meta = message.metadata || {};
  let bodyText = (message.body_text || "").trim();
  let bodyHtml = (meta.body_html || "").trim();

  // Recover from mis-bound saves where reply body was stored in metadata.subject.
  const stray = (meta.subject || meta.reply_subject || "").trim();
  if (!bodyText && !stripHtml(bodyHtml) && stray) {
    const looksLikeBody = stray.length > 80 || stray.includes("\n");
    if (looksLikeBody) {
      bodyText = stray;
      bodyHtml = "";
    }
  }

  if (bodyHtml && !stripHtml(bodyHtml)) {
    bodyHtml = "";
  }

  return { bodyText, bodyHtml };
}

export function composerPayloadFromHtml(html) {
  const safe = sanitizeComposerHtml(html);
  const plain = stripHtml(safe);
  return {
    body_text: plain.trim(),
    body_html: safe || null,
  };
}

/**
 * Apply a document.execCommand while keeping the editor selection.
 * Returns true when the command reported success.
 */
export function runComposerCommand(editor, command, value = null) {
  if (!editor || typeof document === "undefined") return false;
  editor.focus();
  try {
    document.execCommand("defaultParagraphSeparator", false, "p");
  } catch {
    /* older browsers */
  }
  let ok = false;
  try {
    ok = document.execCommand(command, false, value);
  } catch {
    ok = false;
  }
  return Boolean(ok);
}

/** Toggle bullet / numbered list on the current selection or caret block. */
export function toggleComposerList(editor, { ordered = false } = {}) {
  if (!editor) return false;
  const command = ordered ? "insertOrderedList" : "insertUnorderedList";
  const before = editor.innerHTML;
  const ok = runComposerCommand(editor, command);
  if (ok && editor.innerHTML !== before) return true;

  // Fallback when execCommand is a no-op (empty editor / odd selection).
  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0) {
    editor.focus();
  }
  const placeholder = ordered ? "<ol><li><br></li></ol>" : "<ul><li><br></li></ul>";
  if (!editor.innerText?.trim()) {
    editor.innerHTML = placeholder;
    editor.focus();
    return true;
  }
  // Retry after normalizing orphan text nodes into a paragraph.
  try {
    document.execCommand("formatBlock", false, "p");
  } catch {
    /* ignore */
  }
  const retried = runComposerCommand(editor, command);
  return retried || editor.innerHTML !== before;
}
